import { useEffect, useRef, useState } from "react";

import { showIntercomWindow } from "next-intercom";

import { RVButton, RVIconSizeOptions } from "@regal-voice/ui";

import styles from "./DraggableIntercomButton.module.scss";

const DEFAULT_BOTTOM = 20;
const DEFAULT_LEFT = 20;
const DRAG_THRESHOLD_PX = 4;
// Matches $topBarHeight in _component-dimensions.scss; prevents the button from going behind the header
const TOP_BAR_HEIGHT_PX = 64;

export function DraggableIntercomButton(): JSX.Element {
    const [position, setPosition] = useState({ bottom: DEFAULT_BOTTOM, left: DEFAULT_LEFT });
    const isDragging = useRef(false);
    const hasDragged = useRef(false);
    const dragStartRef = useRef({ mouseX: 0, mouseY: 0, bottom: DEFAULT_BOTTOM, left: DEFAULT_LEFT });
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function onMouseMove(e: MouseEvent) {
            if (!isDragging.current) {
                return;
            }
            const dx = e.clientX - dragStartRef.current.mouseX;
            const dy = dragStartRef.current.mouseY - e.clientY;
            if (!hasDragged.current && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
                return;
            }
            hasDragged.current = true;

            const button = containerRef.current;
            const buttonWidth = button?.offsetWidth ?? 0;
            const buttonHeight = button?.offsetHeight ?? 0;
            const maxLeft = Math.max(0, window.innerWidth - buttonWidth);
            const maxBottom = Math.max(0, window.innerHeight - buttonHeight - TOP_BAR_HEIGHT_PX);

            setPosition({
                left: Math.min(Math.max(0, dragStartRef.current.left + dx), maxLeft),
                bottom: Math.min(Math.max(0, dragStartRef.current.bottom + dy), maxBottom),
            });
        }

        function onMouseUp() {
            isDragging.current = false;
        }

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, []);

    function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
        e.preventDefault();
        hasDragged.current = false;
        isDragging.current = true;
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            bottom: position.bottom,
            left: position.left,
        };
    }

    function handleClick() {
        if (!hasDragged.current) {
            showIntercomWindow();
        }
    }

    return (
        <div
            ref={containerRef}
            className={styles.container}
            style={{ bottom: position.bottom, left: position.left }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label="Get Help"
            title="Get Help"
        >
            <RVButton
                className={styles.interbutton}
                type="primary"
                tabIndex={-1}
                icon={"chat-bubble"}
                iconSize={RVIconSizeOptions.LARGE}
            ></RVButton>
        </div>
    );
}
